import { Injectable, OnDestroy } from '@angular/core';
import { DealersDropDown, GroupMember } from '@home/src/app/Interface/Dealer';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  get,
  post,
  setGroupMemberCode,
} from '@home/src/app/core/interceptors/fetch-client';
import {
  CustomGroup,
  ICustomGroupDropdown,
  IPageGroupDropdown,
  PageGroup,
  SelectedCustom,
  IBoCDropdownResponse,
  IBoCDropeown,
} from '@home/src/app/Interface/kpi';
import { ApiUtils } from '@core/services/Utils/apiUtils';
import { environment } from '@env/environment';
import {
  GroupComposite,
  groupCompositeDateResponse,
} from '@home/src/app/Interface/vitals';
import { AuthenticationService } from '../Auth/authservice';

@Injectable({ providedIn: 'root' })
export class GlobalService implements OnDestroy {
  readonly appVersion: string = environment.appVersion;
  // UI + State Subjects
  private readonly groupName = new BehaviorSubject<string | null>(null);
  private readonly selectedRole = new BehaviorSubject<string | null>(null);
  private readonly groupMemberCode = new BehaviorSubject<string>('');
  allDealers = new BehaviorSubject<DealersDropDown[]>([]);
  orgDetails = new BehaviorSubject<GroupMember[]>([]);
  private readonly selectedDealer = new BehaviorSubject<GroupMember | null>(
    null,
  );
  private readonly toastMessage = new BehaviorSubject<string | null>(null);
  private readonly toastType = new BehaviorSubject<
    'success' | 'error' | 'warning' | null
  >(null);
  private readonly isSideMenuClosed = new BehaviorSubject<boolean>(true);
  private readonly showOverlaySpinner = new BehaviorSubject<boolean>(true);
  private readonly startDate = new BehaviorSubject<string | null>(null);
  private readonly breadcrumbSubject = new BehaviorSubject<string>('');
  private readonly activeComponentSubject = new BehaviorSubject<string | null>(
    null,
  );
  private readonly allPageGroups = new BehaviorSubject<PageGroup[]>([]);
  private readonly allCustomGroups = new BehaviorSubject<CustomGroup[]>([]);
  private readonly allCustomGroupMembers = new BehaviorSubject<
    SelectedCustom[]
  >([]);
  private readonly dateDropdownDataSubject = new BehaviorSubject<
    GroupComposite[]
  >([]);
  private readonly bocDropdownDataSubject = new BehaviorSubject<IBoCDropeown[]>(
    [],
  );

  private readonly allOEMDepartments = new BehaviorSubject<any[]>([]);

  readonly selectedDealerChanged = new Subject<GroupMember>();

  // Exposed observables
  readonly groupName$ = this.groupName.asObservable();
  readonly groupMemberCode$ = this.groupMemberCode.asObservable();
  readonly allDealers$ = this.allDealers.asObservable();
  readonly orgDetails$ = this.orgDetails.asObservable();
  readonly selectedDealer$ = this.selectedDealer.asObservable();
  toastMessage$ = this.toastMessage.asObservable();
  toastType$ = this.toastType.asObservable();
  readonly isSideMenuClosed$ = this.isSideMenuClosed.asObservable();
  readonly showOverlaySpinner$ = this.showOverlaySpinner.asObservable();
  readonly startDate$ = this.startDate.asObservable();
  readonly breadcrumb$ = this.breadcrumbSubject.asObservable();
  readonly activeComponent$ = this.activeComponentSubject.asObservable();
  readonly selectedRole$ = this.selectedRole.asObservable();
  readonly dateDropdownData$ = this.dateDropdownDataSubject.asObservable();
  readonly bocDropdownData$ = this.bocDropdownDataSubject.asObservable();
  readonly allPageGroups$ = this.allPageGroups.asObservable();
  readonly allCustomGroups$ = this.allCustomGroups.asObservable();
  readonly allCustomGroupMembers$ = this.allCustomGroupMembers.asObservable();
  readonly allOEMDepartments$ = this.allOEMDepartments.asObservable();

  constructor(private authToken: AuthenticationService) {}

  private currentCustGrpDealerCode: string | null = null;
  private currentGrpCompDealerCode: string | null = null;
  private currentRoleDealerCode: string | null = null;

  // ------------------ UI & Navigation ------------------

  getCSSVariable(varName: string): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
  }

  updateBreadcrumb(message: string): void {
    this.breadcrumbSubject.next(message);
  }

  setActiveComponent(name: string | null): void {
    this.activeComponentSubject.next(name);
  }

  getActiveComponent(): string | null {
    return this.activeComponentSubject.value;
  }

  updateShowOverlaySpinner(val: boolean): void {
    if (val !== this.showOverlaySpinner.value) {
      this.showOverlaySpinner.next(val);
    }
  }

  updateIsSideMenuClosed(val: boolean): void {
    if (val !== this.isSideMenuClosed.value) {
      this.isSideMenuClosed.next(val);
    }
  }

  resetToast(): void {
    this.toastMessage.next(null);
  }

  showToast(message: string, type: 'success' | 'error' | 'warning'): void {
    if (this.toastMessage.value !== message || this.toastType.value !== type) {
      this.toastMessage.next(message);
      this.toastType.next(type);
    }
  }

  // ------------------ Group/Role/Dealer Info ------------------

  updateGroupName(code: string): void {
    this.groupName.next(code);
  }

  updateAllOEMDepartments(depts: any[]): void {
    this.allOEMDepartments.next(depts);
  }

  updateGroupMemberCode(code: string): void {
    this.groupMemberCode.next(code);
  }

  getGroupMemberCode(): string {
    return this.groupMemberCode.value;
  }

  setSelectedRole(role: string): void {
    this.selectedRole.next(role);
  }

  getSelectedRole(): string {
    return this.selectedRole.value ?? 'DLR';
  }

  updateAllDealers(dealers: DealersDropDown[]): void {
    this.allDealers.next(dealers);
  }

  updateGroupMembers(members: GroupMember[]): void {
    this.orgDetails.next(members);
  }

  getSelectedDealer(): GroupMember | null {
    return this.selectedDealer.value;
  }

  updateSelectedDealers(dealer: GroupMember): void {
    if (
      !dealer ||
      dealer.groupMemberCode === this.selectedDealer.value?.groupMemberCode
    )
      return;
    this.selectedDealer.next({ ...dealer });
    this.selectedDealerChanged.next(dealer);
    this.updateGroupMemberCode(dealer.groupMemberCode);
  }

  setDefaultDealer(dealers: GroupMember[]): void {
    const claimsData = this.authToken.getClaimsData();
    const committeeCode = claimsData?.['committeeCode'] ?? '';
    const dealerCode = claimsData?.['dealerCode'] ?? '';
    const sessionGroupMemberCode = committeeCode + dealerCode; // e.g. "A05109"
    console.log(sessionGroupMemberCode);
    // ✅ update groupMemberCode only if sessionGroupMemberCode has length 6
    if (sessionGroupMemberCode.length >= 6) {
      this.updateGroupMemberCode(sessionGroupMemberCode);
    }

    let selected: GroupMember | null = null;
    if (!dealerCode) {
      selected = dealers[0];
    } else {
      selected =
        dealers.find((d) => d.groupMemberCode === this.groupMemberCode.value) ??
        null;
    }

    if (selected) {
      const selecteddealerCode = selected?.groupMemberCode.slice(-2);
      this.authToken.overWriteClaimsData({
        dealerCode: selecteddealerCode,
      });
      setGroupMemberCode(selected.groupMemberCode);
      this.updateSelectedDealers(selected);
      this.updateGroupMemberCode(selected.groupMemberCode);
    }
  }

  // ------------------ Analyst Info ------------------

  async getAnalystInfo(groupMemberCode: string) {
    const response: any = { data: null, statusCode: null };
    const url = `${environment.getAnalystDetails}?Gcode=${groupMemberCode}`;
    await ApiUtils.handleApiResponse1(
      get(url),
      (data, statusCode) => {
        if (statusCode === 200) {
          response.data = data;
        }
        response.statusCode = statusCode;
      },
      'Failed to fetch analyst information:',
    );
    return response;
  }

  // ------------------ Date & Dropdowns ------------------

  updateStartDate(val: string | null): void {
    if (val !== this.startDate.value) {
      this.startDate.next(val);
    }
  }

  async fetchDateDropdownData(): Promise<void> {
    if (
      this.currentGrpCompDealerCode ===
      this.selectedDealer.value?.groupMemberCode
    )
      return;
    await ApiUtils.handleApiResponse1(
      get<groupCompositeDateResponse>(environment.getGroupCompositeDates),
      (data) => {
        const groupComposites = data?.data?.groupComposites ?? [];
        this.dateDropdownDataSubject.next(groupComposites);
        this.currentGrpCompDealerCode =
          this.selectedDealer.value?.groupMemberCode || null;
      },
      'Failed to fetch dropdown dates',
    );
  }

  async getBocDropdownsData(groupCompositeId: number): Promise<void> {
    await ApiUtils.handleApiResponse1(
      get<IBoCDropdownResponse>(
        `${environment.getBocDropdownsData}?groupCompositeId=${groupCompositeId}`,
      ),
      (data) => {
        const bocDropdownData = data?.data ?? [];
        this.bocDropdownDataSubject.next(bocDropdownData);
        this.currentGrpCompDealerCode =
          this.selectedDealer.value?.groupMemberCode || null;
      },
      'Failed to fetch dropdown dates',
    );
  }

  async getDynamicDates(years: number): Promise<any> {
    const url = `${environment.getDynamicDates}?numberOfyears=${years}`;
    let response: any = { data: null, statusCode: null };
    try {
      await ApiUtils.handleApiResponse1(
        get<groupCompositeDateResponse>(url),
        (data, statusCode) => {
          response.data = data;
          response.statusCode = statusCode;
        },
        'Failed to fetch dropdown dates',
      );
    } catch (error: any) {
      response = { data: error, statusCode: error.status || error.statusCode };
    }
    return response;
  }

  getDateDropdown(): GroupComposite[] {
    return this.dateDropdownDataSubject.value;
  }

  // ------------------ Page & Custom Group Fetch ------------------

  async getOEMDepartments(GroupCompId: number | null = null): Promise<void> {
    let url = environment.getOEMDepartments;
    if (GroupCompId) url += `?groupCompositeId=${GroupCompId}`;
    await ApiUtils.handleApiResponse1(
      get<any>(url),
      (data, statusCode) => {
        if (statusCode !== 200) {
          this.updateAllOEMDepartments([]);
        } else {
          this.updateAllOEMDepartments(data ?? []);
        }
      },
      'Failed to fetch Page Groups',
    );
  }

  // ------------------ Page & Custom Group Fetch ------------------

  async fetchPageGroups(): Promise<void> {
    if (
      this.currentRoleDealerCode === this.selectedDealer.value?.groupMemberCode
    )
      return;
    await ApiUtils.handleApiResponse1(
      get<IPageGroupDropdown>(environment.getPageGrouping),
      (data) => {
        const pageGroups = data?.data ?? [];
        this.updateDropDownData(this.allPageGroups, pageGroups);
        this.currentRoleDealerCode =
          this.selectedDealer.value?.groupMemberCode || null;
      },
      'Failed to fetch Page Groups',
    );
  }

  async fetchCustomGroups(
    force: boolean = false,
    groupCompId: number | undefined = undefined,
  ): Promise<void> {
    if (
      !force &&
      this.currentCustGrpDealerCode ===
        this.selectedDealer.value?.groupMemberCode
    )
      return;
    let url = environment.getCustomGroups;
    if (groupCompId !== undefined) {
      url += `?groupCompositeId=${groupCompId}`;
    }
    await ApiUtils.handleApiResponse1(
      get<ICustomGroupDropdown>(url),
      (data) => {
        if (!data) {
          console.warn('No data received');
          return;
        }
        this.updateDropDownData(this.allCustomGroups, data.customGroups);
        this.updateDropDownData(
          this.allCustomGroupMembers,
          data.customGrouping_Members,
        );
        this.currentCustGrpDealerCode =
          this.selectedDealer.value?.groupMemberCode || null;
      },
      'Failed to fetch Custom groups:',
    );
  }

  async getApiDataWithReturn<T>(
    url: string,
    errorMessage: string = 'Failed to fetch data:',
  ): Promise<T | null> {
    try {
      const response = await get<T>(url);
      return response.parsedBody ?? null;
    } catch (error) {
      console.error(errorMessage, error);
      return null;
    }
  }

  async getOrgDetailsByGroupCode(): Promise<any> {
    let response = null;
    try {
      await ApiUtils.handleApiResponse1(
        get<any>(environment.getOrgDetailsByGroupCode),
        (data, statusCode) => {
          response = { data, statusCode };
        },
        'Failed to fetch Custom groups:',
      );
    } catch (error: any) {
      response = { data: error, statusCode: error.status || error.statusCode };
    }
    return response;
  }

  async getPagesById(url: any): Promise<any> {
    let response = null;
    try {
      await ApiUtils.handleApiResponse1(
        get<any>(url),
        (data, statusCode) => {
          response = { data, statusCode };
        },
        'Failed to fetch Pages by ID:',
      );
    } catch (error: any) {
      response = { data: error, statusCode: error.status || error.statusCode };
    }
    return response;
  }

  async getColumnsWithlines(url: any): Promise<any> {
    let response = null;
    try {
      await ApiUtils.handleApiResponse1(
        get<any>(url),
        (data, statusCode) => {
          response = { data, statusCode };
        },
        'Failed to fetch Columns by ID:',
      );
    } catch (error: any) {
      response = { data: error, statusCode: error.status || error.statusCode };
    }
    return response;
  }

  async sendMailToAnalyst(data: any): Promise<any> {
    const url = `${environment.sendMailToAnalyst}`;
    let response = null;
    try {
      await ApiUtils.handleApiResponse1(
        post<any>(url, data),
        (data, statusCode) => {
          response = { data, statusCode };
          console.log('Mail sent to analyst successfully', { response });
        },
        'Failed to send mail to analyst:',
      );
    } catch (error: any) {
      response = { data: error, statusCode: error.status || error.statusCode };
    }
    return response;
  }

  async sendMailForSupport(data: any): Promise<any> {
    const url = `${environment.sendMailForSupport}`;
    let response = null;
    try {
      await ApiUtils.handleApiResponse1(
        post<any>(url, data),
        (data, statusCode) => {
          response = { data, statusCode };
          console.log('Mail sent to support successfully', { response });
        },
        'Failed to send mail to analyst:',
      );
    } catch (error: any) {
      response = { data: error, statusCode: error.status || error.statusCode };
    }
    return response;
  }

  // ------------------ Utility ------------------

  private updateDropDownData<T>(
    subject: BehaviorSubject<T[]>,
    data: T[],
  ): void {
    subject.next(data);
  }

  // ------------------ Lifecycle ------------------

  ngOnDestroy(): void {
    this.groupName.complete();
    this.selectedRole.complete();
    this.groupMemberCode.complete();
    this.allDealers.complete();
    this.orgDetails.complete();
    this.selectedDealer.complete();
    this.toastMessage.complete();
    this.toastType.complete();
    this.isSideMenuClosed.complete();
    this.showOverlaySpinner.complete();
    this.startDate.complete();
    this.breadcrumbSubject.complete();
    this.activeComponentSubject.complete();
    this.allPageGroups.complete();
    this.allCustomGroups.complete();
    this.allCustomGroupMembers.complete();
    this.dateDropdownDataSubject.complete();
    this.selectedDealerChanged.complete();
  }
}
